import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { comics, statusLabels } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';

function formatViews(views: number): string {
  if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return views.toLocaleString();
}

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find(c => c.slug === slug);

  useEffect(() => {
    if (!comic) {
      document.title = '未找到 - 韩国漫画大全';
      return;
    }
    document.title = `${comic.title} - 韩国漫画大全 | 免费在线韩漫阅读网`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `${comic.title}，${comic.description.slice(0, 100)}。韩国漫画大全免费在线韩漫阅读平台，提供${comic.title}免费在线阅读。`);
    }
  }, [comic]);

  if (!comic) {
    return (
      <div className="px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
          <i className="ri-error-warning-line text-2xl"></i>
        </div>
        <p className="text-foreground-500">未找到该漫画</p>
        <Link to="/" className="mt-4 inline-block text-sm text-accent-500 hover:text-accent-600">
          返回首页
        </Link>
      </div>
    );
  }

  const relatedComics = comics
    .filter(c => c.id !== comic.id && (c.categorySlug === comic.categorySlug || c.tags?.some(t => comic.tags?.includes(t))))
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.category, href: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: i === 0 ? comic.updateDate : `2026-${String(Math.max(1, 7 - Math.floor(i / 3))).padStart(2, '0')}-${String(Math.max(1, Math.floor(Math.random() * 28) + 1)).padStart(2, '0')}`,
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.updateDate,
    image: comic.coverImage,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画大全',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是什么类型的漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}是一部${comic.category}类韩漫，标签包括${(comic.tags || []).join('、')}，由${comic.author}创作。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}更新进度如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}目前${statusLabels[comic.status]}，共有${comic.chapters}话。${
            comic.status === 'serializing' ? '正在持续更新中，请关注最新话。' : '已全部完结，可以放心阅读。'
          }`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}可以在哪里免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}可以在韩国漫画大全网站上免费在线阅读，支持电脑和手机端访问。`,
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `http://w340q.cn/${item.href}` : undefined,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <Breadcrumb items={breadcrumbItems} />

      <article className="px-4 md:px-6 py-6 md:py-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12">
            <div className="md:w-72 shrink-0">
              <div className="rounded-xl overflow-hidden bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === 'serializing'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-300 text-foreground-600'
                }`}>
                  {statusLabels[comic.status]}
                </span>
                {comic.tags?.map(tag => (
                  <span key={tag} className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950">
                {comic.title}
              </h1>

              <div className="flex items-center gap-4 mt-3 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {comic.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400"></i>
                  <strong className="text-foreground-800">{comic.rating}</strong>
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(comic.views)} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-3-line"></i>
                  共 {comic.chapters} 话
                </span>
              </div>

              <div className="mt-6 p-5 rounded-xl bg-background-100">
                <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
                  <i className="ri-play-circle-line"></i>
                  开始阅读
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i>
                  加入收藏
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">章节列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {chapters.map(ch => (
                <Link
                  key={ch.num}
                  to={`/comic/${comic.slug}/chapter/${ch.num}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200/60 transition-colors group"
                >
                  <span className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400">{ch.date}</span>
                </Link>
              ))}
            </div>
            {comic.chapters > 20 && (
              <div className="text-center mt-4">
                <p className="text-sm text-foreground-400">
                  仅显示最近 20 话 · 全部 {comic.chapters} 话
                </p>
              </div>
            )}
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-16">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map(c => (
                  <ComicCard key={c.id} comic={c} variant="default" />
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}